"use client";

import ClickOutside from "@faire/web--source/ui/ClickOutside";
import * as React from "react";

import { ClearButton } from "./__internal__/ClearButton";
import { DropdownContent } from "./__internal__/DropdownContent";
import { DropdownInput } from "./__internal__/DropdownInput";
import { MobileDropdown } from "./__internal__/MobileDropdown";
import { useDropdownInteractions } from "./__internal__/useDropdownInteractions";
import { useDropdownState } from "./__internal__/useDropdownState";
import { isCustomOption } from "./__internal__/utils";
import { Wrapper } from "./styles";
import { IProps } from "./types";

/**
 * @deprecated use `Select` from `@faire/slate/components/select` - since 2025-03-28
 *
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/dropdown)
 */
export const Dropdown = <T extends string = string>(props: IProps<T>) => {
  const {
    options,
    filter,
    onSelect,
    value,
    containingElement,
    disableInput,
    className,
    placeholder,
    label,
    error,
    helper,
    createNewOption,
    disabled,
    autoComplete = "off",
    id,
    inputRef,
    borderless,
    customOption,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    "data-hj-allow": dataHjAllow,
    useTabletAndAboveDropdownStyle,
    onDropdownOpen,
    onDropdownClose,
    onClear,
    customOptionMaxLength,
  } = props;

  const {
    dropdownId,
    targetRef,
    refs,
    open,
    setOpen,
    searchValue,
    setSearchValue,
    highlightIndex,
    setHighlightIndex,
    isCustomizingOption,
    setIsCustomizingOption,
    filteredOptions,
    ariaProps,
    showErrorState,
    showErrorMessage,
    errorMessage,
    showMobileDropdown,
  } = useDropdownState({
    id,
    inputRef,
    filter,
    options,
    customOption,
    createNewOption,
    ariaLabel,
    label,
    error,
    useTabletAndAboveDropdownStyle,
  });

  const {
    closeDropdown,
    handleClickOutside,
    handleInputClick,
    handleInputKeyDown,
    handleKeyDown,
    handleValueSelect,
    handleOptionSelect,
    handleInputSearch,
    handleCustomOptionEdit,
    resetSearchValue,
  } = useDropdownInteractions({
    createNewOption,
    customOption,
    filteredOptions,
    highlightIndex,
    isCustomizingOption,
    onDropdownClose,
    onDropdownOpen,
    onSelect,
    open,
    options,
    refs,
    searchValue,
    setHighlightIndex,
    setIsCustomizingOption,
    setOpen,
    setSearchValue,
    value,
    targetRef,
  });

  if (showMobileDropdown) {
    return (
      <MobileDropdown
        borderless={borderless}
        className={className}
        dataTestId={dataTestId}
        disabled={disabled}
        errorMessage={errorMessage}
        options={options}
        helper={helper}
        id={dropdownId}
        label={label}
        onOpen={onDropdownOpen}
        onSelect={handleValueSelect}
        placeholder={placeholder}
        showErrorMessage={showErrorMessage}
        showErrorState={showErrorState}
        value={value}
      />
    );
  }

  return (
    <Wrapper
      data-test-id={`dropdown-${label || "default"}`}
      className={className}
      $disabled={disabled}
      $disableInput={disableInput}
      {...ariaProps}
    >
      <ClickOutside
        onClickOutside={() => handleClickOutside()}
        onKeyDown={handleKeyDown}
      >
        <DropdownInput
          id={dropdownId}
          dataTestId={dataTestId}
          disabled={disabled}
          label={label}
          helper={helper}
          error={error}
          placeholder={placeholder}
          disableSearch={disableInput}
          onKeyDown={handleInputKeyDown}
          targetRef={targetRef}
          borderless={borderless}
          autoComplete={autoComplete}
          ariaLabel={ariaLabel}
          value={value}
          dataHjAllow={dataHjAllow}
          onClear={onClear}
          maxLength={customOptionMaxLength}
          onClick={handleInputClick}
          options={filteredOptions}
          searchValue={searchValue}
          onSearch={handleInputSearch}
        />
        {open ? (
          <DropdownContent
            createNewOption={createNewOption}
            searchValue={searchValue}
            closeDropdown={closeDropdown}
            resetSearchValue={resetSearchValue}
            showErrorMessage={showErrorMessage}
            helper={helper}
            open={open}
            filteredOptions={filteredOptions}
            isCustomOption={isCustomOption}
            onSelect={handleOptionSelect}
            highlightIndex={highlightIndex}
            onHighlight={setHighlightIndex}
            refs={refs}
            targetRef={targetRef}
            containingElement={containingElement}
            options={options}
            onCustomOptionEdit={handleCustomOptionEdit}
          />
        ) : null}
      </ClickOutside>
      <ClearButton value={value} onClear={onClear} helper={helper} />
    </Wrapper>
  );
};

Dropdown.displayName = "Dropdown";
